import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft, Download } from "lucide-react";
import {
  CASE_STUDIES,
  PROFILE,
  ASSETS,
  EXPERIENCE,
  EDUCATION,
  CERTS,
  TOOLKIT,
  DELIVERABLES,
  TESTIMONIALS,
  STRENGTHS_PROFILE,
  CV_METRICS,
} from "@/data/portfolio";
import { track } from "@/lib/analytics";
import OnePager, { Block } from "@/components/site/OnePager";
import { DA } from "@/data/da";
import { DA_SITE } from "@/data/daSite";
import { KOLDING_CV } from "@/data/kolding";

const FEATURED = CASE_STUDIES.filter((c) => !c.secondary);
const TOTAL = FEATURED.length + 2;

export default function PortfolioPdf() {
  const [params] = useSearchParams();
  const onlyCv = params.get("only") === "cv";
  const noCv = params.get("nocv") === "1";
  const variant = params.get("for") || "";
  const lang = params.get("lang") === "da" || ["kolding", "proces"].includes(variant) ? "da" : "en";
  useEffect(() => {
    document.title = onlyCv ? "CV, Sinthu Nathan" : "Sinthu in 5 pages, portfolio + CV, Sinthu Nathan";
  }, [onlyCv]);

  return (
    <div data-testid="bundle-page" className="min-h-screen bg-[#e9e4dc] print:bg-white py-8 print:py-0">
      <div className="no-print max-w-[210mm] mx-auto mb-4 flex items-center justify-between gap-3 px-2 flex-wrap">
        <Link to="/" data-testid="bundle-back" className="inline-flex items-center gap-2 text-sm text-[#55625b] hover:text-[#9a7218]">
          <ArrowLeft className="h-4 w-4" /> Back to portfolio
        </Link>
        <div className="flex items-center gap-2">
          <a
            href={ASSETS.cvOriginal}
            target="_blank"
            rel="noreferrer"
            download
            data-testid="bundle-cv-original"
            onClick={() => track("cv_download", { from: "bundle" }, "/")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[#14352a] text-sm font-medium hover:bg-[#14352a] hover:text-white transition-colors"
          >
            <Download className="h-4 w-4" /> Original CV
          </a>
          {onlyCv && (
            <div data-testid="lang-toggle" className="inline-flex rounded-full border border-[#14352a] overflow-hidden text-xs font-medium">
              {["en", "da"].map((l) => (
                <Link key={l} to={`/portfolio-pdf?only=cv&lang=${l}`} data-testid={`lang-${l}`} className={`px-3 h-10 inline-flex items-center ${lang === l ? "bg-[#14352a] text-white" : "hover:bg-[#f2ece4]"}`}>
                  {l.toUpperCase()}
                </Link>
              ))}
            </div>
          )}
          <button
            data-testid="bundle-download-btn"
            onClick={() => {
              track("bundle_download", {}, "/");
              window.print();
            }}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full btn-guide text-sm font-medium"
          >
            <Printer className="h-4 w-4" /> Save all {TOTAL} pages as PDF
          </button>
        </div>
      </div>

      <div className="space-y-6 print:space-y-0">
        {!onlyCv && <CoverPage noCv={noCv} />}
        {!onlyCv &&
          FEATURED.map((cs, i) => (
            <OnePager key={cs.slug} cs={cs} pageNo={i + 2} last={noCv && i === FEATURED.length - 1} />
          ))}
        {!noCv && <CvPage standalone={onlyCv} lang={lang} variant={variant} />}
      </div>
    </div>
  );
}

function CoverPage({ noCv }) {
  const pages = noCv ? TOTAL - 1 : TOTAL;
  const piotr = TESTIMONIALS.find((t) => t.name === "Piotr M. Klima");
  return (
    <article
      data-testid="bundle-page-1"
      className="print-sheet sheet-break mx-auto bg-white text-[#14352a] w-[210mm] h-[297mm] overflow-hidden p-[14mm] shadow-xl print:shadow-none grid grid-rows-[auto_1fr_auto] gap-6"
    >
      <header className="border-b-2 border-[#14352a] pb-3 flex items-center justify-between">
        <span className="font-mono-label text-[9px] text-[#9a7218]">{noCv ? `Portfolio · ${pages} pages` : `Sinthu in ${pages} pages · portfolio + CV`}</span>
        <span className="font-mono-label text-[9px] text-[#86928a]">{PROFILE.domain}</span>
      </header>

      <main className="grid grid-cols-12 gap-6 content-start">
        <div className="col-span-7">
          <h1 className="font-display text-[44px] leading-[1.02]">
            Customer Insights &amp; <em className="italic text-[#9a7218]">Growth</em> Researcher
          </h1>
          <p className="mt-5 text-[11px] leading-relaxed">
            Global discovery research to define customer needs, pain points and opportunities · customer journey maps, archetypes and requirements per target group · analytics in digital products for continuous improvement · the community and feedback loop. Data-driven, iterative, human in the loop.
          </p>
        </div>
        <div className="col-span-5 flex flex-col items-end">
          <div className="polaroid !rotate-2 w-[62mm]">
            <div className="aspect-[4/5] overflow-hidden bg-[#f2ece4]">
              <img src={ASSETS.portrait} alt="Sinthu Nathan" className="w-full h-full object-cover" />
            </div>
            <div className="mt-2 font-display italic text-[11px]">Sinthu, in the field</div>
          </div>
          <div className="mt-6 text-right font-mono-label text-[8px] text-[#86928a] leading-relaxed">
            {PROFILE.name}<br />{PROFILE.location}<br />{PROFILE.phone} · {PROFILE.email}<br />{PROFILE.linkedin.replace("https://", "")}
          </div>
        </div>

        <div className="col-span-12 grid grid-cols-12 gap-6 mt-2">
          <Block n=", " title="Contents" className="col-span-5">
            <ol className="space-y-1.5">
              <li className="flex justify-between gap-3"><span>Cover &amp; commercial impact</span><span className="font-mono-label text-[8px] text-[#86928a]">p.1</span></li>
              {FEATURED.map((cs, i) => (
                <li key={cs.slug} className="flex justify-between gap-3">
                  <span>{cs.label.split(" / ")[1] || cs.label}, {cs.short || cs.title}</span>
                  <span className="font-mono-label text-[8px] text-[#86928a] shrink-0">p.{i + 2}</span>
                </li>
              ))}
              {!noCv && <li className="flex justify-between gap-3"><span>CV, experience, education, toolkit</span><span className="font-mono-label text-[8px] text-[#86928a]">p.{TOTAL}</span></li>}
            </ol>
          </Block>
          <Block n=", " title="Commercial impact" className="col-span-7">
            <ul className="space-y-1.5">
              <li className="flex justify-between gap-3"><span>Value-proposition benchmarking model used by the CEO</span><span className="font-mono-label text-[8px] text-[#86928a] shrink-0">Universal Robots</span></li>
              <li className="flex justify-between gap-3"><span>Segment discovery: plant visits, 200+ interviews, 3 continents</span><span className="font-mono-label text-[8px] text-[#86928a] shrink-0">Universal Robots</span></li>
              <li className="flex justify-between gap-3"><span>Growth loop built with AI: sold out, NPS +66, reach +1,186%</span><span className="font-mono-label text-[8px] text-[#86928a] shrink-0">Tennis Club Odense</span></li>
            </ul>
          </Block>
        </div>

        <div data-testid="cover-strengths" className="col-span-12 flex items-center gap-3 border-y border-[#e5e0d8] py-2.5">
          <span className="font-mono-label text-[8px] text-[#86928a] shrink-0">Top 5 CliftonStrengths®</span>
          <ol className="flex flex-wrap gap-1.5">
            {STRENGTHS_PROFILE.top5.map((s, i) => (
              <li key={s.name} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f2ece4] text-[9.5px] text-[#14352a]">
                <span className="font-mono-label text-[7px] text-[#9a7218]">0{i + 1}</span>{s.name}
              </li>
            ))}
          </ol>
          <span className="ml-auto font-mono-label text-[7.5px] text-[#86928a] shrink-0">Gallup · Nov 2024 · leads with Influencing</span>
        </div>

        {piotr && (
          <figure className="col-span-12 bg-[#14352a] text-[#faf8f5] p-4 border-l-4 border-[#d9a92e]">
            <blockquote className="font-display italic text-[13px] leading-snug">&ldquo;{piotr.pull} This is what outstanding researchers, analysts and strategists do!&rdquo;</blockquote>
            <figcaption className="mt-1.5 font-mono-label text-[8px] text-[#faf8f5]/60">{piotr.name} · {piotr.role} · verbatim LinkedIn recommendation</figcaption>
          </figure>
        )}
      </main>

      <footer className="border-t border-[#e5e0d8] pt-3 flex items-center justify-between font-mono-label text-[8px] text-[#86928a]">
        <span>Interactive version, live analytics and the AI guide: {PROFILE.domain}</span>
        <span>page 1</span>
      </footer>
    </article>
  );
}

const CV_EN = {
  kicker: "Curriculum vitae",
  role: "Customer Insights & Growth Researcher",
  languages: PROFILE.languages.map((l) => `${l.name} (${l.level})`).join(" · "),
  portfolio: `Portfolio: https://${PROFILE.domain}`,
  blocks: ["My experience", "Education", "Certifications", "Selected outcomes", "My deliverables", "My tool kit", "The insightful influencer", "Languages"],
  profile: PROFILE.about,
  experience: EXPERIENCE.map((e) => e.body),
  strengthsNote: "Gallup CliftonStrengths 34 · Top 5",
  metrics: CV_METRICS,
  quote: { text: "This is what outstanding researchers, analysts and strategists do!", who: "Piotr M. Klima · Pricing Director, Universal Robots · verbatim" },
  outcomes: [
    "Value-proposition benchmarking (Power BI) adopted from PM to CEO.",
    "Global discovery, plant visits, 200+ interviews, 3 continents, shaped the roadmap.",
    "Training Partner research: 'a tangible impact on our decision-making processes'.",
    "Research system built from scratch with AI: sold out, NPS +66, reach +1,186%.",
  ],
  mentor: "mentor",
  colleague: "colleague",
  voluntary: "Voluntary",
  focus: EXPERIENCE[0].focus,
  footer: `Interactive portfolio, case studies and verbatim recommendations: https://${PROFILE.domain}`,
  page: "CV · 1 / 1",
};

const localPeriod = (p, t) => {
  if (!t.present) return p;
  let out = p.replace("present", t.present);
  Object.entries(t.months || {}).forEach(([en, da]) => (out = out.replace(en, da)));
  return out;
};

function CvPage({ standalone, lang = "en", variant = "" }) {
  const t = ["kolding", "proces"].includes(variant) ? KOLDING_CV : lang === "da" ? DA.cv : CV_EN;
  const tr = (s) => (lang === "da" ? DA_SITE[s] || s : s);
  const metrics = t.metrics || CV_EN.metrics;
  return (
    <article
      data-testid={`bundle-page-${TOTAL}`}
      className="print-sheet mx-auto bg-white text-[#14352a] w-[210mm] h-[297mm] overflow-hidden shadow-xl print:shadow-none flex flex-col"
    >
      <header data-testid="cv-header" className="bg-[#14352a] text-[#faf8f5] px-[14mm] py-[10mm]">
        <div className="flex items-center gap-6">
          <img src="/portrait.jpg" alt={PROFILE.name} className="h-[28mm] w-[28mm] object-cover rounded-full border-2 border-[#d9a92e] shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-[34px] leading-none tracking-tight">{PROFILE.name}</h1>
            <p className="mt-2 text-[13px] text-[#d9a92e]">{t.role}</p>
            <p className="mt-3 font-mono-label text-[8px] text-[#faf8f5]/70 leading-[1.9]">
              {PROFILE.location} · {PROFILE.phone} · {PROFILE.email}<br />
              {PROFILE.linkedin.replace("https://www.", "")} · {PROFILE.domain}
            </p>
          </div>
        </div>
      </header>

      <section data-testid="cv-metrics" className="grid grid-cols-4 border-b border-[#e5e0d8] divide-x divide-[#e5e0d8] px-[14mm]">
        {metrics.map((m, i) => (
          <div key={i} className={`py-5 ${i === 0 ? "" : "pl-5"}`}>
            <div className="font-display text-[24px] leading-none">{m.value}</div>
            <div className="mt-2 text-[8.5px] text-[#55625b] leading-snug">{m.label}</div>
          </div>
        ))}
      </section>

      <main className="flex-1 grid grid-cols-[1fr_62mm] gap-10 px-[14mm] pt-7 pb-4 text-[10px] leading-[1.55] min-h-0">
        <div className="space-y-7 min-w-0">
          <p data-testid="cv-profile" className="text-[11px] leading-[1.65]">{tr(t.profile)}</p>

          <section>
            <CvHead title={t.blocks[0]} />
            <ol className="mt-3 space-y-4">
              {EXPERIENCE.map((e, i) => (
                <li key={i} data-testid={`cv-exp-${i}`}>
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-semibold text-[11px]">{t.roles?.[e.role] || e.role}<span className="font-normal text-[#55625b]"> · {e.org}{e.note && <span className="text-[#86928a]"> · {t.voluntary}</span>}</span></span>
                    <span className="font-mono-label text-[7.5px] text-[#86928a] shrink-0">{localPeriod(e.period, t)}</span>
                  </div>
                  <p className="mt-1 text-[#55625b]">{tr(t.experience[i])}</p>
                </li>
              ))}
            </ol>
          </section>

          <section>
            <CvHead title={t.blocks[1]} />
            <ul className="mt-3 space-y-2">
              {EDUCATION.map((e, i) => (
                <li key={i} className="flex items-baseline justify-between gap-4">
                  <span className="font-semibold">{t.education?.[e.title] || e.title}<span className="font-normal text-[#55625b]"> · {e.org}</span></span>
                  <span className="font-mono-label text-[7.5px] text-[#86928a] shrink-0">{e.period}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <CvHead title={t.blocks[2]} />
            <ul className="mt-3 space-y-1 text-[#55625b]">
              {CERTS.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </section>
        </div>

        <div className="space-y-7 min-w-0">
          <section data-testid="cv-deliverables">
            <CvHead title={t.blocks[4]} />
            <ul className="mt-3 space-y-1">
              {(t.deliverables || DELIVERABLES.flatMap((col) => col.items).map((it) => tr(it.title))).map((it) => (
                <li key={it}>{it}</li>
              ))}
            </ul>
          </section>

          <section data-testid="cv-toolkit">
            <CvHead title={t.blocks[5]} />
            <ul className="mt-3 space-y-2">
              {TOOLKIT.map((g) => (
                <li key={g.group}>
                  <div className="font-semibold">{tr(g.group)}</div>
                  <div className="text-[#55625b]">{g.items.join(", ")}</div>
                </li>
              ))}
            </ul>
          </section>

          <section data-testid="cv-strengths">
            <CvHead title={t.blocks[6]} />
            <p className="mt-3 text-[#55625b]">{STRENGTHS_PROFILE.top5.map((s) => s.name).join(", ")}</p>
            <p className="mt-1 font-mono-label text-[7.5px] text-[#86928a]">{t.strengthsNote}</p>
          </section>

          <section data-testid="cv-languages">
            <CvHead title={t.blocks[7]} />
            <ul className="mt-3 space-y-1 text-[#55625b]">
              {PROFILE.languages.map((l) => (
                <li key={l.name}>{tr(l.name)} · {tr(l.level)}</li>
              ))}
            </ul>
          </section>
        </div>
      </main>

      <footer className="mx-[14mm] mb-[8mm] border-t border-[#e5e0d8] pt-3 flex items-center justify-between font-mono-label text-[7.5px] text-[#86928a]">
        <span>{t.footer}</span>
        <span>{standalone ? t.page : `page ${TOTAL}`}</span>
      </footer>
    </article>
  );
}

function CvHead({ title }) {
  return <h2 className="font-mono-label text-[8.5px] text-[#9a7218] border-b border-[#e5e0d8] pb-1.5">{title}</h2>;
}
